<script lang="ts" setup>
import { computed, watch } from 'vue'

import { Coordinates } from '@collector/shared-ui-domain'
import {
  useBlockades,
  useEventIncidentsQueue,
  useIncidentName,
} from '@desktop/views/Relation/Sports/composables'

import MouseBallPosition from './MouseBallPosition.vue'
import { useIncidentsRequirePointingPosition } from './useIncidentsRequirePointingPosition'

interface Emits {
  visible: [value: boolean]
}

interface Props {
  ballSrc: string
}

defineProps<Props>()

const emit = defineEmits<Emits>()

// composables
const { getIncidentName } = useIncidentName()
const eventIncidentsQueue = useEventIncidentsQueue()
const {
  currentIncident,
  init,
  getTransformedCoordinates,
  getChainedIncident,
  removeIncidentId,
} = useIncidentsRequirePointingPosition()
const { isPitchBlocked } = useBlockades()

// model
const coordinates = defineModel<Coordinates | null>('coordinates', {
  default: null,
})

// computed
const isVisible = computed(() =>
  isPitchBlocked() ? false : currentIncident.value !== null,
)

// init
init()

watch(
  isVisible,
  (newValue) => {
    emit('visible', newValue)
  },
  { immediate: true },
)

watch(coordinates, (newValue) => {
  if (!newValue) {
    return
  }

  if (currentIncident.value) {
    const { x: x_pos, y: y_pos } = getTransformedCoordinates(
      newValue,
      currentIncident.value,
    )

    eventIncidentsQueue.updateIncident(
      {
        x_pos,
        y_pos,
      },
      currentIncident.value,
    )

    const chainedIncident = getChainedIncident()

    if (chainedIncident) {
      eventIncidentsQueue.updateIncident(
        {
          x_pos,
          y_pos,
        },
        chainedIncident,
      )
    }

    removeIncidentId(currentIncident.value.id)
    coordinates.value = null
  }
})
</script>

<template>
  <div
    v-if="isVisible"
    class="absolute h-full w-full"
  >
    <div
      class="@2md:text-xl text-neutral-light-10/50 absolute h-full w-full text-center text-sm select-none"
    >
      <div
        v-if="currentIncident"
        class="pt-6 font-bold"
      >
        {{ getIncidentName(currentIncident.incident_id) }}
      </div>
      <div>Point out the position on the field</div>
    </div>
    <MouseBallPosition :ballSrc />
  </div>
</template>
